import { useState } from "react";
import { styled } from "@glitz/react";
import { media } from "@glitz/core";
import {
  spacing,
  white,
  fontSize,
  fontWeight,
  green,
  darkGreen,
  lightGrey,
  black,
  grey,
  darkGrey,
} from "../theme";

const API_BASE = process.env.REACT_APP_API_URL || "";

type SubscribeResult = {
  pending?: boolean;
  alreadySubscribed?: boolean;
};

type NewsletterSignupProps = {
  variant?: "footer" | "modal";
  onSubscribe?: (result: SubscribeResult) => void;
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const NewsletterSignup = ({
  variant = "footer",
  onSubscribe,
}: NewsletterSignupProps) => {
  const isModal = variant === "modal";
  const Section = isModal ? ModalSection : FooterSection;

  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const trimmed = email.trim();
    if (!trimmed) {
      setError("Ange e-postadress");
      setSuccess("");
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError("Ange en giltig e-postadress");
      setSuccess("");
      return;
    }

    setError("");
    setSuccess("");
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, website, source: variant }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Kunde inte prenumerera just nu");
      }

      if (data.pending) {
        setSuccess(
          isModal
            ? "Tack! Kolla inkorgen och bekräfta prenumerationen – då skickar vi din rabattkod."
            : "Tack! Kolla inkorgen och bekräfta prenumerationen."
        );
      } else if (data.alreadySubscribed) {
        setSuccess("Du prenumererar redan på vårt nyhetsbrev.");
      } else {
        setSuccess(
          isModal
            ? "Tack! Du är nu prenumerant – kolla inkorgen efter din rabattkod."
            : "Tack! Du är nu prenumerant."
        );
      }

      setEmail("");
      onSubscribe?.({
        pending: Boolean(data.pending),
        alreadySubscribed: Boolean(data.alreadySubscribed),
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Något gick fel – försök igen senare"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Section>
        {isModal ? (
          <ModalSuccessText>{success}</ModalSuccessText>
        ) : (
          <FooterSuccessText>{success}</FooterSuccessText>
        )}
      </Section>
    );
  }

  return (
    <Section id="mc_embed_signup">
      {!isModal && <FooterTitle>Nyhetsbrev</FooterTitle>}
      <SignupForm onSubmit={handleSubmit} noValidate>
        <FieldRow>
          {isModal ? (
            <ModalEmailInput
              type="email"
              name="EMAIL"
              id="mce-EMAIL-modal"
              required
              placeholder="Din e-postadress"
              autoComplete="email"
              aria-label="E-postadress"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          ) : (
            <FooterEmailInput
              type="email"
              name="EMAIL"
              id="mce-EMAIL"
              required
              placeholder="Din e-postadress"
              autoComplete="email"
              aria-label="E-postadress"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          )}
          <SubmitButton
            type="submit"
            value={submitting ? "Skickar…" : "Prenumerera"}
            disabled={submitting}
          />
        </FieldRow>
        {error &&
          (isModal ? (
            <ModalErrorText>{error}</ModalErrorText>
          ) : (
            <FooterErrorText>{error}</FooterErrorText>
          ))}
        <Honeypot
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </SignupForm>
    </Section>
  );
};

const FooterSection = styled.div({
  width: "100%",
  maxWidth: "480px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.s,
  padding: { x: spacing.m },
});

const ModalSection = styled.div({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const FooterTitle = styled.h2({
  color: white,
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
});

const SignupForm = styled.form({
  width: "100%",
});

const FieldRow = styled.div({
  display: "flex",
  flexDirection: "row",
  gap: spacing.s,
  width: "100%",
  ...media({ maxWidth: "520px" }, {
    flexDirection: "column",
  }),
});

const FooterEmailInput = styled.input({
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
  padding: { xy: spacing.s },
  fontSize: fontSize.s,
  border: { xy: { style: "solid", color: "transparent", width: "1px" } },
  borderRadius: "4px",
  backgroundColor: lightGrey,
  color: black,
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
  ":disabled": {
    opacity: 0.7,
  },
});

const ModalEmailInput = styled.input({
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
  padding: { xy: spacing.s },
  fontSize: fontSize.s,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "4px",
  backgroundColor: white,
  color: black,
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
  ":disabled": {
    opacity: 0.7,
  },
});

const SubmitButton = styled.input({
  padding: { x: spacing.l, y: spacing.s },
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: white,
  backgroundColor: green,
  border: { xy: { style: "solid", color: green, width: "2px" } },
  borderRadius: "4px",
  cursor: "pointer",
  transitionProperty: "background-color, border-color",
  transitionDuration: "0.2s",
  ":hover": {
    backgroundColor: darkGreen,
    border: { xy: { style: "solid", color: darkGreen, width: "2px" } },
  },
  ":disabled": {
    opacity: 0.7,
    cursor: "not-allowed",
  },
});

const FooterErrorText = styled.p({
  margin: { top: spacing.xs },
  fontSize: fontSize.s,
  color: "#FF4D4D",
  textAlign: "center",
});

const ModalErrorText = styled.p({
  margin: { top: spacing.xs },
  fontSize: fontSize.s,
  color: "#CC0000",
});

const FooterSuccessText = styled.p({
  fontSize: fontSize.s,
  lineHeight: 1.5,
  color: white,
  textAlign: "center",
});

const ModalSuccessText = styled.p({
  fontSize: fontSize.s,
  lineHeight: 1.5,
  color: darkGrey,
});

const Honeypot = styled.input({
  position: "absolute",
  left: "-5000px",
});

export default NewsletterSignup;
